import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { FlagIcon } from "@heroicons/react/24/solid";
import cn from "classnames";
import { InvestigationLead, InvestigationTable, SpreadsheetLeadAnchor } from "../../../db/schema";
import { revealProps } from "../../common/Reveal";
import LeadFlagPopover, { LeadHover } from "../LeadFlagPopover";
import { api } from "../api";

const PAGE_SIZE = 100;

// Which promoted role a column carries, so the header can badge it. These
// are the columns the chat's query_spreadsheet tool filters and sums on.
function roleFor(table: InvestigationTable, header: string): string | null {
  const roles = table.column_roles || {};
  if (roles.date === header) return "date";
  if (roles.amount === header) return "amount";
  if (roles.counterparty === header) return "counterparty";
  return null;
}

function anchorOf(l: InvestigationLead): SpreadsheetLeadAnchor | null {
  return (l.sheet_anchor as SpreadsheetLeadAnchor | null) || null;
}

export type SpreadsheetViewerHandle = {
  // Jump to a lead's anchor: switch sheet if needed, page to its first
  // flagged row, and center it — the sheet analogue of transcript seek().
  focusLead: (lead: InvestigationLead) => void;
};

/**
 * Paginated read-only view of a parsed spreadsheet. Rows come from the
 * server (investigation_table_rows), never from re-parsing the file, so
 * what's shown is exactly what the chat's queries run against.
 *
 * Flagged rows/columns mirror InvestigationTranscript: severity-tinted
 * highlight, hover shows the lead, and the rail's flag cards drive the
 * highlight through activeLeadId / focusLead.
 */
const SpreadsheetViewer = forwardRef<
  SpreadsheetViewerHandle,
  {
    investigationId?: string;
    // Supplies rows without an investigation (the Files library, where the
    // sheet is read straight from the stored file).
    fetchRows?: (tableId: string, offset: number, limit: number) => Promise<{ rows: { row_index: number; data: Record<string, any> }[]; total: number }>;
    tables: InvestigationTable[];
    selectedTableId: string | null;
    onSelectTable: (id: string) => void;
    leads?: InvestigationLead[];
    // Lead hovered in the insights rail: its rows get the deeper "active" shade.
    activeLeadId?: string | null;
    // Leads under the pointer in the grid, so the rail can light their cards.
    onHoverLeads?: (ids: string[]) => void;
  }
>(({ investigationId, fetchRows, tables, selectedTableId, onSelectTable, leads = [], activeLeadId, onHoverLeads }, ref) => {
  const table = tables.find((t) => t.id === selectedTableId) || tables[0] || null;
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState<{ row_index: number; data: Record<string, any> }[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hoverFlag, setHoverFlag] = useState<LeadHover | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  // focusLead across a sheet switch or page change: remember where to land.
  const pendingOffset = useRef<number | null>(null);
  const pendingScrollRow = useRef<number | null>(null);

  useEffect(() => {
    setOffset(pendingOffset.current ?? 0);
    pendingOffset.current = null;
  }, [table?.id]);

  useEffect(() => {
    if (!table) return;
    let cancelled = false;
    setLoading(true);
    (fetchRows ? fetchRows(table.id, offset, PAGE_SIZE) : api.tableRows(investigationId!, table.id, offset, PAGE_SIZE))
      .then((d) => {
        if (cancelled) return;
        setRows(d.rows);
        setTotal(d.total);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [investigationId, table?.id, offset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Land a deliberate jump mid-pane once its rows are in, like the
  // transcript's seek() centering.
  useEffect(() => {
    const target = pendingScrollRow.current;
    if (target == null || loading) return;
    pendingScrollRow.current = null;
    scrollRef.current?.querySelector<HTMLElement>(`[data-row="${target}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [rows, loading]);

  const focusLead = (lead: InvestigationLead) => {
    const a = anchorOf(lead);
    if (!a) return;
    const targetTable = tables.find((t) => t.id === a.tableId) || tables.find((t) => t.sheet_name === a.sheet);
    if (!targetTable) return;
    const first = a.rows?.[0];
    const targetOffset = first != null ? Math.floor(first / PAGE_SIZE) * PAGE_SIZE : 0;
    if (first != null) pendingScrollRow.current = first;
    if (targetTable.id !== table?.id) {
      pendingOffset.current = targetOffset;
      onSelectTable(targetTable.id);
    } else if (targetOffset !== offset && first != null) {
      setOffset(targetOffset);
    } else if (first != null) {
      scrollRef.current?.querySelector<HTMLElement>(`[data-row="${first}"]`)?.scrollIntoView({ block: "center", behavior: "smooth" });
      pendingScrollRow.current = null;
    } else if (a.column) {
      scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };
  useImperativeHandle(ref, () => ({ focusLead }));

  if (!table) {
    return <div className="p-6 text-base-content/70">No parsed sheets yet.</div>;
  }
  const header = table.header_row || [];
  const from = offset + 1;
  const to = Math.min(offset + rows.length, total);

  // Leads anchored to the visible sheet, split into row and column flags.
  const sheetLeads = leads.filter((l) => {
    if (l.status === "dismissed") return false;
    const a = anchorOf(l);
    return a && (a.tableId === table.id || (!a.tableId && a.sheet === table.sheet_name));
  });
  const rowFlags = new Map<number, InvestigationLead[]>();
  const columnFlags = new Map<string, InvestigationLead[]>();
  for (const l of sheetLeads) {
    const a = anchorOf(l)!;
    for (const r of a.rows || []) rowFlags.set(r, [...(rowFlags.get(r) || []), l]);
    if (a.column) {
      const h = header.find((c) => c.toLowerCase() === a.column!.toLowerCase());
      if (h) columnFlags.set(h, [...(columnFlags.get(h) || []), l]);
    }
  }

  const showFlags = (flagged: InvestigationLead[], el: HTMLElement) => {
    const r = el.getBoundingClientRect();
    const below = r.bottom + 220 < window.innerHeight;
    setHoverFlag({ leads: flagged, x: Math.min(r.left + 56, window.innerWidth - 400), y: below ? r.bottom + 6 : r.top - 6, below });
    onHoverLeads?.(flagged.map((f) => f.id));
  };
  const clearFlags = () => {
    setHoverFlag(null);
    onHoverLeads?.([]);
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      {tables.length > 1 && (
        <div className="px-4 pt-3 flex items-center gap-1 flex-wrap shrink-0">
          {tables.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onSelectTable(t.id)}
              className={cn(
                "px-2.5 py-1 rounded-lg text-xs font-medium transition-colors",
                t.id === table.id
                  ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300"
                  : "text-base-content/60 hover:bg-base-200 hover:text-base-content"
              )}
            >
              {t.sheet_name || "Sheet"} <span className="tabular-nums opacity-60">{(t.row_count || 0).toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-auto p-4">
        {/* Keyed by sheet so switching tabs remounts the card and replays the fade. */}
        <div key={table.id} {...revealProps(0)} className="reveal rounded-xl border border-base-300 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table table-xs">
              <thead className="sticky top-0 z-10 bg-base-200">
                <tr>
                  <th className="text-base-content/40">#</th>
                  {header.map((h) => {
                    const role = roleFor(table, h);
                    const colLeads = columnFlags.get(h);
                    const colSev = colLeads ? Math.max(...colLeads.map((f) => f.severity)) : 0;
                    return (
                      <th
                        key={h}
                        className={cn(
                          "whitespace-nowrap",
                          colLeads && (colSev >= 4 ? "bg-red-50 dark:bg-red-950/30" : "bg-amber-50 dark:bg-amber-950/30")
                        )}
                        onMouseEnter={colLeads ? (e) => showFlags(colLeads, e.currentTarget) : undefined}
                        onMouseLeave={colLeads ? clearFlags : undefined}
                      >
                        {colLeads && <FlagIcon className={cn("inline size-3 mr-1 -mt-px", colSev >= 4 ? "text-red-500" : "text-amber-500")} />}
                        {h}
                        {role && (
                          <span className="ml-1.5 text-[10px] font-medium normal-case px-1 py-px rounded bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300">
                            {role}
                          </span>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody className={cn(loading && "opacity-50")}>
                {rows.map((r) => {
                  const segFlags = rowFlags.get(r.row_index);
                  const flagged = Boolean(segFlags?.length);
                  const topSeverity = segFlags ? Math.max(...segFlags.map((f) => f.severity)) : 0;
                  const active = Boolean(activeLeadId && segFlags?.some((f) => f.id === activeLeadId));
                  return (
                    <tr
                      key={`${r.row_index}-${offset}`}
                      data-row={r.row_index}
                      onMouseEnter={segFlags?.length ? (e) => showFlags(segFlags, e.currentTarget) : undefined}
                      onMouseLeave={segFlags?.length ? clearFlags : undefined}
                      className={cn(
                        "transition-colors",
                        !flagged && "hover",
                        flagged &&
                          topSeverity >= 4 &&
                          (active
                            ? "bg-red-200 dark:bg-red-900/60"
                            : "bg-red-50 dark:bg-red-950/30 hover:bg-red-100 dark:hover:bg-red-950/50"),
                        flagged &&
                          topSeverity < 4 &&
                          (active
                            ? "bg-amber-200 dark:bg-amber-900/60"
                            : "bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50")
                      )}
                    >
                      {/* Accent bar as an inset shadow (a border would shift the column). */}
                      <td
                        className={cn(
                          "text-base-content/40 tabular-nums",
                          flagged && (topSeverity >= 4 ? "shadow-[inset_3px_0_0_#ef4444]" : "shadow-[inset_3px_0_0_#f59e0b]")
                        )}
                      >
                        <span className="inline-flex items-center gap-1">
                          {flagged && <FlagIcon className={cn("size-3", topSeverity >= 4 ? "text-red-500" : "text-amber-500")} />}
                          {r.row_index + 1}
                        </span>
                      </td>
                      {header.map((h) => {
                        const colLeads = columnFlags.get(h);
                        const colSev = colLeads ? Math.max(...colLeads.map((f) => f.severity)) : 0;
                        return (
                          <td
                            key={h}
                            className={cn(
                              "max-w-xs truncate",
                              roleFor(table, h) === "amount" && "tabular-nums text-right",
                              !flagged && colLeads && (colSev >= 4 ? "bg-red-50/70 dark:bg-red-950/20" : "bg-amber-50/70 dark:bg-amber-950/20")
                            )}
                          >
                            {r.data[h] == null ? "" : String(r.data[h])}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="px-4 pb-3 flex items-center gap-3 text-xs text-base-content/60 shrink-0">
        <span className="tabular-nums">
          Rows {total ? from.toLocaleString() : 0}–{to.toLocaleString()} of {total.toLocaleString()}
        </span>
        <div className="inline-flex rounded-md border border-base-300 overflow-hidden">
          <button
            type="button"
            disabled={offset === 0 || loading}
            onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            className="px-1.5 py-1 hover:bg-base-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            title="Previous page"
          >
            <ChevronLeftIcon className="size-3.5" />
          </button>
          <button
            type="button"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset(offset + PAGE_SIZE)}
            className="px-1.5 py-1 border-l border-base-300 hover:bg-base-200 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
            title="Next page"
          >
            <ChevronRightIcon className="size-3.5" />
          </button>
        </div>
        <span className="ml-auto">Ask the chat for totals or date ranges — those are computed by the database, not estimated.</span>
      </div>
      {hoverFlag && (
        <LeadFlagPopover
          hover={hoverFlag}
          corner={(l) => {
            const a = anchorOf(l);
            return a?.rows?.length ? `${a.rows.length} row${a.rows.length === 1 ? "" : "s"}` : a?.column || "";
          }}
        />
      )}
    </div>
  );
});

export default SpreadsheetViewer;
